/**
 * Dutchy's accessibility fixes.
 * Kept in one file, separate from Horizon's own code, so the fixes survive theme updates.
 */

/**
 * Fix: hidden carousel slides (WCAG 2.4.3 / 4.1.2).
 * Horizon marks off-screen slides aria-hidden="true", but their links and buttons stay in
 * the tab order, so keyboard users land on content they can't see. Take those controls out
 * of the tab order while their slide is hidden and put them back when it becomes visible.
 */
(() => {
  const FOCUSABLE =
    'a[href], button, input, select, textarea, iframe, summary, [tabindex], video[controls], audio[controls], [contenteditable="true"]';
  const SAVED = 'data-a11y-tabindex';
  const HIDDEN_SLIDE = 'slideshow-slide[aria-hidden="true"]';

  const sync = () => {
    for (const el of document.querySelectorAll(`slideshow-slide ${FOCUSABLE}, [${SAVED}]`)) {
      const inHiddenSlide = el.closest(HIDDEN_SLIDE) !== null;

      if (inHiddenSlide && !el.hasAttribute(SAVED)) {
        el.setAttribute(SAVED, el.getAttribute('tabindex') ?? '');
        el.setAttribute('tabindex', '-1');
      } else if (!inHiddenSlide && el.hasAttribute(SAVED)) {
        const original = el.getAttribute(SAVED);
        original ? el.setAttribute('tabindex', original) : el.removeAttribute('tabindex');
        el.removeAttribute(SAVED);
      }
    }
  };

  let queued = false;
  const queueSync = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  };

  new MutationObserver(queueSync).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });

  queueSync();
})();

/**
 * Fix: moving video in slideshows (WCAG 2.2.2 / 1.1.1).
 * Horizon's "Pause slideshow" button only stops the slides rotating; background videos keep
 * looping. Make Pause/Play control the videos too, start paused for visitors who ask their
 * device to reduce motion, and mark each video's fallback image as decorative.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const videosIn = (slideshow) => slideshow.querySelectorAll('video[autoplay]');

  const syncVideos = (slideshow) => {
    const paused = slideshow.hasAttribute('paused');
    for (const video of videosIn(slideshow)) {
      if (paused) video.pause();
      else video.play().catch(() => {});
    }
  };

  const setup = () => {
    for (const img of document.querySelectorAll('video img:not([alt])')) img.setAttribute('alt', '');

    for (const slideshow of document.querySelectorAll('slideshow-component')) {
      if (!videosIn(slideshow).length || slideshow.hasAttribute('data-a11y-video')) continue;
      slideshow.setAttribute('data-a11y-video', '');

      new MutationObserver(() => syncVideos(slideshow)).observe(slideshow, {
        attributes: true,
        attributeFilter: ['paused'],
      });

      if (reduceMotion.matches) {
        if (typeof slideshow.pause === 'function') slideshow.pause();
        else slideshow.setAttribute('paused', '');
      }
      syncVideos(slideshow);
    }
  };

  // Slideshow elements upgrade asynchronously, so wait for the page to finish loading.
  if (document.readyState === 'complete') setup();
  else window.addEventListener('load', setup);
})();

/**
 * Fix: menu and filter list structure (WCAG 1.3.1 / 4.1.2).
 * <overflow-list> slots its children into a <ul>, but Horizon marks the menu's <li>s
 * role="presentation" (so the list has no items), gives the "More" button role="menuitem"
 * with no menu around it, and slots non-<li> filter groups straight into the list.
 */
(() => {
  const fix = () => {
    for (const list of document.querySelectorAll('overflow-list')) {
      for (const child of list.children) {
        if (child.tagName === 'LI') {
          if (child.getAttribute('role') === 'presentation') child.removeAttribute('role');
        } else if (!child.hasAttribute('role')) {
          child.setAttribute('role', 'listitem');
        }
      }
    }

    for (const item of document.querySelectorAll('[role="menuitem"]')) {
      if (!item.closest('[role="menu"], [role="menubar"], [role="group"]')) item.removeAttribute('role');
    }

    // Loading placeholders carry an aria-label that a plain <div> can't have; they're decorative.
    for (const skeleton of document.querySelectorAll('.product-recommendations__skeleton-item[aria-label]')) {
      skeleton.removeAttribute('aria-label');
      skeleton.setAttribute('aria-hidden', 'true');
    }
  };

  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      fix();
    });
  }).observe(document.documentElement, { subtree: true, childList: true });

  fix();
})();

/**
 * Fix: slideshow dot state (WCAG 4.1.2).
 * Horizon sets aria-selected on the dot buttons, which buttons can't have. Mirror it to
 * aria-current, which screen readers announce as "current", and drop aria-selected.
 * dutchys-a11y.css carries the matching active-dot style.
 */
(() => {
  const DOT = '.slideshow-controls__dots button';

  const fix = (dot) => {
    if (!dot.hasAttribute('aria-selected')) return;
    if (dot.getAttribute('aria-selected') === 'true') dot.setAttribute('aria-current', 'true');
    else dot.removeAttribute('aria-current');
    dot.removeAttribute('aria-selected');
  };

  new MutationObserver((mutations) => {
    for (const { target } of mutations) {
      if (target instanceof Element && target.matches(DOT)) fix(target);
    }
    document.querySelectorAll(DOT).forEach(fix);
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-selected'],
  });

  document.querySelectorAll(DOT).forEach(fix);
})();

/**
 * Fix: product photo gallery (WCAG 2.1.1).
 * With more than one photo the gallery is a sideways-scrolling strip that Horizon keeps out of
 * the tab order (tabindex="-1"), so keyboard users can't scroll it directly. When it actually
 * scrolls, make it a labelled, focusable region; the browser then scrolls it with the arrow keys.
 */
(() => {
  const SCROLLER = 'media-gallery slideshow-slides';

  const fix = () => {
    for (const scroller of document.querySelectorAll(SCROLLER)) {
      if (scroller.scrollWidth > scroller.clientWidth + 1) {
        scroller.setAttribute('tabindex', '0');
        scroller.setAttribute('role', 'region');
        scroller.setAttribute('aria-label', 'Product photos');
      } else if (scroller.getAttribute('role') === 'region') {
        scroller.setAttribute('tabindex', '-1');
        scroller.removeAttribute('role');
        scroller.removeAttribute('aria-label');
      }
    }
  };

  if (document.readyState === 'complete') fix();
  else window.addEventListener('load', fix);
  window.addEventListener('resize', fix);
})();
